import { Component, input, output, inject, effect, computed, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { CatalogService } from '@services/data/catalog.service';
import { flattenMenusAsTree, menuKey, menuParentId } from '../../utils/menu-tree.util';

@Component({
    selector: 'app-role-menus-form',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, NgSelectModule],
    templateUrl: './role-menus-form.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './role-menus-form.component.css'
})
export class RoleMenusFormComponent {
    menusAll = input.required<any[]>();
    currentMenusIds = input<any[]>([]);

    onSave = output<any[]>();
    onCancel = output<void>();

    formRole: FormGroup;
    private fb = inject(FormBuilder);
    public catalogService = inject(CatalogService);
    checkedAll = false;
    private lastIdsKey = '';
    private selected = signal<string[]>([]);

    processedMenus = computed(() => {
        const all = flattenMenusAsTree(this.menusAll() || []);
        const byKey = new Map(all.map((m) => [menuKey(m), m]));
        return all.map((m) => {
            const parent = byKey.get(menuParentId(m) || '');
            return {
                ...m,
                key: menuKey(m),
                label: String(m.name || m.title || m.path || menuKey(m)),
                groupLabel: parent
                    ? String(parent.name || parent.title || menuKey(parent))
                    : 'Menús principales'
            };
        });
    });

    compareById = (a: any, b: any) => menuKey(a) === menuKey(b);

    constructor() {
        this.formRole = this.fb.group({ menus: [[]] });
        effect(() => {
            const source = this.processedMenus();
            const ids = this.currentMenusIds();
            const key = [...(ids || [])].map((i) => menuKey(i)).sort().join(',');
            if (source.length === 0 || key === this.lastIdsKey) return;
            this.lastIdsKey = key;
            this.applyCurrentIds(source, ids);
        });
    }

    onMenusChange(value: any) {
        this.selected.set(this.normalizeKeys(value));
        this.updateCheckedAll();
    }

    selectedIds(): any[] {
        const tracked = this.selected();
        if (tracked.length) return tracked;
        return this.normalizeKeys(this.formRole.get('menus')?.value);
    }

    confirm() {
        this.onSave.emit(this.selectedIds());
    }

    cancel() {
        this.onCancel.emit();
    }

    changeAll() {
        if (this.checkedAll) {
            this.formRole.get('menus')?.setValue([]);
            this.selected.set([]);
            this.checkedAll = false;
            return;
        }
        const all = this.processedMenus();
        this.formRole.get('menus')?.setValue([...all]);
        this.selected.set(all.map((m) => menuKey(m)));
        this.checkedAll = true;
    }

    private applyCurrentIds(source: any[], ids: any[]) {
        const idSet = new Set((ids || []).map((i) => String(typeof i === 'object' ? menuKey(i) : i)));
        const selected = source.filter((m) => idSet.has(menuKey(m)));
        this.formRole.get('menus')?.setValue(selected, { emitEvent: false });
        this.selected.set(selected.map((m) => menuKey(m)));
        this.updateCheckedAll();
    }

    private updateCheckedAll() {
        const source = this.processedMenus();
        const selected = this.selectedIds();
        this.checkedAll = source.length > 0 && selected.length === source.length;
    }

    private normalizeKeys(val: any): string[] {
        if (!Array.isArray(val)) return [];
        return val.map((item) => String(typeof item === 'object' ? menuKey(item) : item)).filter(Boolean);
    }
}
